import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Search, 
  User, 
  Calendar, 
  MapPin, 
  Filter, 
  Download, 
  TrendingUp, 
  DollarSign, 
  Users, 
  ShoppingBag,
  SortAsc,
  SortDesc,
  RefreshCw,
  BarChart3,
  PieChart,
  Eye,
  FileText
} from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';

interface BillingRecord {
  id: string;
  customerId: string;
  customerName: string;
  tableId?: string;
  tableNumber?: string;
  tableLocation?: string;
  totalAmount: number;
  paymentMethod: string;
  transactionId?: string | null;
  orderIds: string[];
  numberOfOrders: number;
  totalItems: number;
  paidAt: string;
  sessionStartTime?: string;
}

const AdminCustomerBilling: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBilling, setSelectedBilling] = useState<BillingRecord | null>(null);
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Enhanced filtering and sorting states
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'customer' | 'orders'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table' | 'grouped'>('cards');
  const [hideTestRecords, setHideTestRecords] = useState(false);
  
  // Ref for tracking previous billing history count
  const previousCountRef = React.useRef(0);

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  // Load billing history from Firestore with real-time updates
  React.useEffect(() => {
    const loadBillingHistory = async () => {
      try {
        console.log('🔄 AdminCustomerBilling: Starting to load billing history...');
        console.log('🔐 AdminCustomerBilling: Checking authentication...');
        
        // Check authentication status
        const { auth } = await import('../../firebase/config');
        const currentUser = auth.currentUser;
        console.log('👤 AdminCustomerBilling: Current user:', currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified
        } : 'Not authenticated');
        
        setIsLoading(true);
        const { FirestoreService } = await import('../../firebase/firestore');
        
        console.log('📖 AdminCustomerBilling: Attempting to read customerBillingHistory collection...');
        const history = await FirestoreService.getCustomerBillingHistory();
        
        console.log('📊 AdminCustomerBilling: Loaded billing history successfully');
        console.log('📊 AdminCustomerBilling: Number of records:', history.length);
        console.log('📊 AdminCustomerBilling: Sample records:', history.slice(0, 3));
        
        setBillingHistory(history);
        
        if (history.length === 0) {
          console.log('⚠️ AdminCustomerBilling: No billing records found.');
          console.log('💡 AdminCustomerBilling: Possible reasons:');
          console.log('   1. No customers have completed payments yet');
          console.log('   2. Firestore rules are blocking record creation');
          console.log('   3. Customer payment process is failing silently');
          console.log('📖 AdminCustomerBilling: See CUSTOMER_BILLING_DEBUG_FIX.md for troubleshooting');
        } else {
          console.log('✅ AdminCustomerBilling: Found billing records from customers:');
          const uniqueCustomers = Array.from(new Set(history.map(r => r.customerName)));
          console.log('👥 AdminCustomerBilling: Customers:', uniqueCustomers);
        }
      } catch (error) {
        console.error('❌ AdminCustomerBilling: Error loading billing history:', error);
        
        if (error instanceof Error) {
          console.error('❌ AdminCustomerBilling: Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
          
          // Provide specific troubleshooting based on error type
          if (error.message.includes('Missing or insufficient permissions')) {
            console.error('🔒 AdminCustomerBilling: FIRESTORE SECURITY RULES ERROR');
            console.error('💡 AdminCustomerBilling: Update Firestore rules to allow read access to customerBillingHistory');
            console.error('📖 AdminCustomerBilling: See CUSTOMER_BILLING_DEBUG_FIX.md for the solution');
          } else if (error.message.includes('auth')) {
            console.error('🔐 AdminCustomerBilling: AUTHENTICATION ERROR');
            console.error('💡 AdminCustomerBilling: Make sure you are logged in as an admin');
          } else if (error.message.includes('network')) {
            console.error('🌐 AdminCustomerBilling: NETWORK ERROR');
            console.error('💡 AdminCustomerBilling: Check internet connection and Firebase configuration');
          }
        }
      } finally {
        setIsLoading(false);
        console.log('✅ AdminCustomerBilling: Loading process complete');
      }
    };

    loadBillingHistory();

    // Set up real-time updates (refresh every 30 seconds)
    const interval = setInterval(async () => {
      try {
        console.log('🔄 AdminCustomerBilling: Auto-refreshing billing history...');
        const { FirestoreService } = await import('../../firebase/firestore');
        const history = await FirestoreService.getCustomerBillingHistory();
        setBillingHistory(history);
        console.log('🔄 AdminCustomerBilling: Auto-refresh complete -', history.length, 'records');
      } catch (error) {
        console.error('❌ AdminCustomerBilling: Error in auto-refresh:', error);
      }
    }, 30000);

    // Listen for new billing records from customer payments
    const handleNewBillingRecord = (event: CustomEvent) => {
      console.log('🔔 AdminCustomerBilling: New billing record event received:', event.detail);
      // Refresh the billing history when a new record is created
      setTimeout(async () => {
        try {
          const { FirestoreService } = await import('../../firebase/firestore');
          const history = await FirestoreService.getCustomerBillingHistory();
          setBillingHistory(history);
          console.log('🔄 AdminCustomerBilling: Refreshed after new billing record');
        } catch (error) {
          console.error('❌ AdminCustomerBilling: Error refreshing after new record:', error);
        }
      }, 2000); // Wait 2 seconds for Firestore to sync
    };

    // Add event listener for new billing records
    window.addEventListener('newBillingRecord', handleNewBillingRecord as EventListener);

    return () => {
      clearInterval(interval);
      window.removeEventListener('newBillingRecord', handleNewBillingRecord as EventListener);
    };
  }, []);

  // Notification for new records
  React.useEffect(() => {
    if (billingHistory.length > previousCountRef.current && previousCountRef.current > 0) {
      const newRecordsCount = billingHistory.length - previousCountRef.current;
      console.log(`🔔 AdminCustomerBilling: ${newRecordsCount} new billing record(s) added`);
      
      // You could add a toast notification here
      // For now, we'll just log it
    }
    
    previousCountRef.current = billingHistory.length;
  }, [billingHistory]);

  // Enhanced filtering and sorting logic
  const filteredAndSortedBillingHistory = useMemo(() => {
    let filtered = billingHistory.filter(record => {
      // Filter out test records (optional - can be toggled)
      const isTestRecord = 
        record.customerId?.startsWith('TEST_CUSTOMER_') ||
        record.customerId?.startsWith('DIAGNOSTIC_TEST_') ||
        record.customerName?.includes('Test Customer') ||
        record.customerName?.includes('Diagnostic Test Customer');
      
      // Hide test records if toggle is enabled
      if (hideTestRecords && isTestRecord) return false;
      
      // Search filter
      const matchesSearch = record.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.tableNumber && record.tableNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        record.customerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.transactionId && record.transactionId.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Date range filter
      const recordDate = new Date(record.paidAt);
      const matchesDateRange = (!dateRange.start || recordDate >= new Date(dateRange.start)) &&
        (!dateRange.end || recordDate <= new Date(dateRange.end + 'T23:59:59'));
      
      // Payment method filter
      const matchesPaymentMethod = paymentMethodFilter === 'all' || record.paymentMethod === paymentMethodFilter;
      
      return matchesSearch && matchesDateRange && matchesPaymentMethod;
    });

    // Add test record indicator to remaining records
    filtered = filtered.map(record => ({
      ...record,
      isTestRecord: record.customerId?.startsWith('TEST_CUSTOMER_') ||
                   record.customerId?.startsWith('DIAGNOSTIC_TEST_') ||
                   record.customerName?.includes('Test Customer') ||
                   record.customerName?.includes('Diagnostic Test Customer')
    }));

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.paidAt).getTime() - new Date(b.paidAt).getTime();
          break;
        case 'amount':
          comparison = a.totalAmount - b.totalAmount;
          break;
        case 'customer':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        case 'orders':
          comparison = a.numberOfOrders - b.numberOfOrders;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [billingHistory, searchTerm, dateRange, paymentMethodFilter, sortBy, sortOrder, hideTestRecords]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalRevenue = filteredAndSortedBillingHistory.reduce((sum, record) => sum + record.totalAmount, 0);
    const totalOrders = filteredAndSortedBillingHistory.reduce((sum, record) => sum + record.numberOfOrders, 0);
    const totalItems = filteredAndSortedBillingHistory.reduce((sum, record) => sum + record.totalItems, 0);
    const uniqueCustomers = new Set(filteredAndSortedBillingHistory.map(record => record.customerId)).size;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const averageBillValue = filteredAndSortedBillingHistory.length > 0 ? totalRevenue / filteredAndSortedBillingHistory.length : 0;

    // Payment method breakdown
    const paymentMethodBreakdown = filteredAndSortedBillingHistory.reduce((acc, record) => {
      acc[record.paymentMethod] = (acc[record.paymentMethod] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Daily revenue trend (last 7 days)
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    const dailyRevenue = last7Days.map(date => {
      const dayRevenue = filteredAndSortedBillingHistory
        .filter(record => record.paidAt.startsWith(date))
        .reduce((sum, record) => sum + record.totalAmount, 0);
      return { date, revenue: dayRevenue };
    });

    return {
      totalRevenue,
      totalOrders,
      totalItems,
      uniqueCustomers,
      averageOrderValue,
      averageBillValue,
      paymentMethodBreakdown,
      dailyRevenue,
      totalRecords: filteredAndSortedBillingHistory.length
    };
  }, [filteredAndSortedBillingHistory]);

  // Group billing records by customer name
  const groupedByCustomer = useMemo(() => {
    const groups = filteredAndSortedBillingHistory.reduce((acc, record) => {
      const customerName = record.customerName;
      if (!acc[customerName]) {
        acc[customerName] = {
          customerName,
          customerId: record.customerId,
          records: [],
          totalAmount: 0,
          totalOrders: 0,
          totalItems: 0,
          totalTransactions: 0,
          lastVisit: record.paidAt,
          paymentMethods: new Set()
        };
      }
      
      acc[customerName].records.push(record);
      acc[customerName].totalAmount += record.totalAmount;
      acc[customerName].totalOrders += record.numberOfOrders;
      acc[customerName].totalItems += record.totalItems;
      acc[customerName].totalTransactions += 1;
      acc[customerName].paymentMethods.add(record.paymentMethod);
      
      // Update last visit if this record is more recent
      if (new Date(record.paidAt) > new Date(acc[customerName].lastVisit)) {
        acc[customerName].lastVisit = record.paidAt;
      }
      
      return acc;
    }, {} as Record<string, {
      customerName: string;
      customerId: string;
      records: any[];
      totalAmount: number;
      totalOrders: number;
      totalItems: number;
      totalTransactions: number;
      lastVisit: string;
      paymentMethods: Set<string>;
    }>);

    // Convert to array and sort by total amount (highest spenders first)
    return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredAndSortedBillingHistory]);

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-green-900 text-green-400';
      case 'card':
        return 'bg-blue-900 text-blue-400';
      case 'upi':
        return 'bg-purple-900 text-purple-400';
      default:
        return 'bg-gray-900 text-gray-400';
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case 'cash':
        return '💵 Cash';
      case 'card':
        return '💳 Card';
      case 'upi':
        return '📱 UPI';
      default:
        return method;
    }
  };

  // Export functionality
  const exportToCSV = () => {
    const headers = [
      'Date',
      'Customer Name',
      'Customer ID',
      'Table Number',
      'Table Location',
      'Total Amount',
      'Payment Method',
      'Transaction ID',
      'Number of Orders',
      'Total Items',
      'Session Duration (minutes)'
    ];

    const csvData = filteredAndSortedBillingHistory.map(record => [
      new Date(record.paidAt).toLocaleString(),
      record.customerName,
      record.customerId,
      record.tableNumber || 'N/A',
      record.tableLocation || 'N/A',
      record.totalAmount.toFixed(2),
      record.paymentMethod,
      record.transactionId || 'N/A',
      record.numberOfOrders,
      record.totalItems,
      record.sessionStartTime 
        ? Math.round((new Date(record.paidAt).getTime() - new Date(record.sessionStartTime).getTime()) / 60000)
        : 'N/A'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `billing-history-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateRange({ start: '', end: '' });
    setPaymentMethodFilter('all');
    setSortBy('date');
    setSortOrder('desc');
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="p-2 text-slate-400 hover:text-white mr-4"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Customer Billing History</h1>
              <p className="text-slate-400 text-sm">Manage and analyze customer billing records</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showAnalytics 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <BarChart3 size={16} className="inline mr-2" />
              Analytics
            </button>
            
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
            >
              <Download size={16} className="inline mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        <AnimatePresence>
          {showAnalytics && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-slate-700 bg-slate-750"
            >
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="text-green-400" size={24} />
                      <span className="text-green-400 text-xs font-medium">REVENUE</span>
                    </div>
                    <div className="text-white text-2xl font-bold">${analytics.totalRevenue.toFixed(2)}</div>
                    <div className="text-green-300 text-sm">Avg: ${analytics.averageBillValue.toFixed(2)}/bill</div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <ShoppingBag className="text-blue-400" size={24} />
                      <span className="text-blue-400 text-xs font-medium">ORDERS</span>
                    </div>
                    <div className="text-white text-2xl font-bold">{analytics.totalOrders}</div>
                    <div className="text-blue-300 text-sm">Avg: ${analytics.averageOrderValue.toFixed(2)}/order</div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-900 to-pink-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="text-purple-400" size={24} />
                      <span className="text-purple-400 text-xs font-medium">CUSTOMERS</span>
                    </div>
                    <div className="text-white text-2xl font-bold">{analytics.uniqueCustomers}</div>
                    <div className="text-purple-300 text-sm">{analytics.totalRecords} transactions</div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-900 to-red-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="text-orange-400" size={24} />
                      <span className="text-orange-400 text-xs font-medium">ITEMS</span>
                    </div>
                    <div className="text-white text-2xl font-bold">{analytics.totalItems}</div>
                    <div className="text-orange-300 text-sm">
                      {analytics.totalOrders > 0 ? (analytics.totalItems / analytics.totalOrders).toFixed(1) : 0} items/order
                    </div>
                  </div>
                </div>

                {/* Payment Method Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center">
                      <PieChart size={18} className="mr-2" />
                      Payment Methods
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.paymentMethodBreakdown).map(([method, count]) => (
                        <div key={method} className="flex items-center justify-between">
                          <span className="text-slate-300">{getPaymentMethodText(method)}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-slate-700 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getPaymentMethodColor(method).replace('text-', 'bg-').replace('900', '500')}`}
                                style={{ width: `${(count / analytics.totalRecords) * 100}%` }}
                              />
                            </div>
                            <span className="text-white font-medium text-sm w-8">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-800 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center">
                      <BarChart3 size={18} className="mr-2" />
                      Daily Revenue (Last 7 Days)
                    </h3>
                    <div className="space-y-2">
                      {analytics.dailyRevenue.map((day, index) => (
                        <div key={day.date} className="flex items-center justify-between">
                          <span className="text-slate-300 text-sm">
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-slate-700 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-teal-500"
                                style={{ 
                                  width: `${analytics.totalRevenue > 0 ? (day.revenue / Math.max(...analytics.dailyRevenue.map(d => d.revenue))) * 100 : 0}%` 
                                }}
                              />
                            </div>
                            <span className="text-white font-medium text-sm">${day.revenue.toFixed(0)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="p-6 space-y-6">
        {/* Search and Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Search by customer name, table, transaction ID..."
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                showFilters || dateRange.start || dateRange.end || paymentMethodFilter !== 'all'
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-600'
              }`}
            >
              <Filter size={16} />
              Filters
              {(dateRange.start || dateRange.end || paymentMethodFilter !== 'all') && (
                <span className="bg-white bg-opacity-20 rounded-full px-2 py-1 text-xs">
                  {[dateRange.start && 'Date', paymentMethodFilter !== 'all' && 'Payment'].filter(Boolean).length}
                </span>
              )}
            </button>

            {/* View Mode Toggle */}
            <div className="flex bg-slate-800 border border-slate-600 rounded-lg p-1">
              {(['cards', 'table', 'grouped'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                    viewMode === mode
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {mode === 'cards' ? <Eye size={16} /> : mode === 'table' ? <FileText size={16} /> : <Users size={16} />}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="bg-slate-800 border border-slate-600 rounded-lg p-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Date Range */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Start Date</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">End Date</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  {/* Payment Method Filter */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Payment Method</label>
                    <select
                      value={paymentMethodFilter}
                      onChange={(e) => setPaymentMethodFilter(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="all">All Methods</option>
                      <option value="cash">💵 Cash</option>
                      <option value="card">💳 Card</option>
                      <option value="upi">📱 UPI</option>
                    </select>
                  </div>

                  {/* Sort Options */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Sort By</label>
                    <div className="flex gap-2">
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="date">Date</option>
                        <option value="amount">Amount</option>
                        <option value="customer">Customer</option>
                        <option value="orders">Orders</option>
                      </select>
                      <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded text-slate-300 hover:text-white transition-colors"
                      >
                        {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-600">
                  <span className="text-slate-400 text-sm">
                    Showing {filteredAndSortedBillingHistory.length} of {billingHistory.length} records
                  </span>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Customer Summary */}
        {filteredAndSortedBillingHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800 rounded-lg p-4 mb-6"
          >
            <h3 className="text-white font-semibold text-lg mb-3">👥 Customer Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{analytics.uniqueCustomers}</div>
                <div className="text-slate-400 text-sm">Unique Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{analytics.totalRecords}</div>
                <div className="text-slate-400 text-sm">Total Transactions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  ${analytics.averageBillValue.toFixed(0)}
                </div>
                <div className="text-slate-400 text-sm">Avg per Customer</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">
                  {analytics.totalOrders > 0 ? (analytics.totalOrders / analytics.uniqueCustomers).toFixed(1) : 0}
                </div>
                <div className="text-slate-400 text-sm">Avg Orders/Customer</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Billing History List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">📋 Customer Billing Records</h2>
              <p className="text-slate-400 text-sm">
                {filteredAndSortedBillingHistory.length} of {billingHistory.length} records
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  setIsLoading(true);
                  const { FirestoreService } = await import('../../firebase/firestore');
                  const history = await FirestoreService.getCustomerBillingHistory();
                  setBillingHistory(history);
                  setIsLoading(false);
                }}
                className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                disabled={isLoading}
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={async () => {
                  const { checkAuthStatus } = await import('../../utils/testBillingFirestore');
                  const isAuthenticated = await checkAuthStatus();
                  alert(isAuthenticated ? '✅ User is authenticated! Check console for details.' : '❌ Not authenticated! Please login first.');
                }}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
              >
                🔐 Auth
              </button>
              <button
                onClick={async () => {
                  console.log('🧪 Running Firestore test...');
                  const { testBillingFirestore, checkAuthStatus } = await import('../../utils/testBillingFirestore');
                  
                  // First check authentication
                  const isAuthenticated = await checkAuthStatus();
                  if (!isAuthenticated) {
                    alert('❌ Authentication Error: Please login as an admin user first. Check console for details.');
                    return;
                  }
                  
                  // Run the test
                  const success = await testBillingFirestore();
                  if (success) {
                    alert('✅ Test passed! Check console for details. Click Refresh to see the test record.');
                  } else {
                    alert('❌ Test failed! Check console for detailed error information and solutions.');
                  }
                }}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors"
              >
                🧪 Test
              </button>
              <button
                onClick={async () => {
                  console.log('🔍 Running comprehensive billing diagnostic...');
                  const { runBillingDiagnostic } = await import('../../utils/billingDiagnostic');
                  
                  const results = await runBillingDiagnostic();
                  
                  if (results.errors.length === 0) {
                    alert('🎉 Diagnostic passed! Billing system is working correctly. Check console for details.');
                  } else {
                    alert(`⚠️ Found ${results.errors.length} issue(s). Check console for detailed analysis and solutions.`);
                  }
                }}
                className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-lg transition-colors"
              >
                🔍 Diagnose
              </button>
              <button
                onClick={async () => {
                  console.log('🧪 Testing complete customer billing flow...');
                  const { testCustomerBillingFlow } = await import('../../utils/testCustomerBilling');
                  
                  const success = await testCustomerBillingFlow();
                  
                  if (success) {
                    alert('✅ Customer billing flow test passed! Check console for details. Click Refresh to see the test record.');
                  } else {
                    alert('❌ Customer billing flow test failed! Check console for detailed error information and solutions.');
                  }
                }}
                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
              >
                🧪 Flow Test
              </button>
              <button
                onClick={async () => {
                  console.log('🔍 Identifying real vs test customer records...');
                  const { identifyRealCustomerRecords } = await import('../../utils/cleanupTestRecords');
                  
                  const result = await identifyRealCustomerRecords();
                  
                  if (result.success) {
                    alert(`📊 Analysis complete! Found ${result.realRecords?.length || 0} real customers and ${result.testRecords} test records. Check console for details.`);
                  } else {
                    alert('❌ Analysis failed! Check console for error details.');
                  }
                }}
                className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-lg transition-colors"
              >
                📊 Analyze
              </button>
              <button
                onClick={async () => {
                  console.log('🧹 Identifying test records for cleanup...');
                  const { cleanupTestBillingRecords } = await import('../../utils/cleanupTestRecords');
                  
                  const result = await cleanupTestBillingRecords();
                  
                  if (result.success) {
                    alert(`🧹 Cleanup analysis complete! Found ${result.testRecords?.length || 0} test records. Check console for cleanup instructions.`);
                  } else {
                    alert('❌ Cleanup analysis failed! Check console for error details.');
                  }
                }}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
              >
                🧹 Cleanup
              </button>
              <button
                onClick={() => setHideTestRecords(!hideTestRecords)}
                className={`px-3 py-2 text-white text-sm rounded-lg transition-colors ${
                  hideTestRecords 
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {hideTestRecords ? '👁️ Show Test' : '🙈 Hide Test'}
              </button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="text-center py-12 text-slate-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
              Loading billing history...
            </div>
          ) : viewMode === 'grouped' ? (
            /* Grouped View - Customer-wise organization */
            <div className="space-y-4">
              {groupedByCustomer.map((customerGroup, groupIndex) => (
                <motion.div
                  key={customerGroup.customerId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.1 }}
                  className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden"
                >
                  {/* Customer Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-teal-600 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                          <User className="text-white" size={20} />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-xl">{customerGroup.customerName}</h3>
                          <p className="text-white text-sm opacity-90">ID: {customerGroup.customerId}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-bold text-2xl">${customerGroup.totalAmount.toFixed(2)}</div>
                        <div className="text-white text-sm opacity-90">{customerGroup.totalTransactions} visits</div>
                      </div>
                    </div>
                    
                    {/* Customer Statistics */}
                    <div className="grid grid-cols-4 gap-4 mt-4">
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">{customerGroup.totalTransactions}</div>
                        <div className="text-white text-xs opacity-75">Visits</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">{customerGroup.totalOrders}</div>
                        <div className="text-white text-xs opacity-75">Orders</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">{customerGroup.totalItems}</div>
                        <div className="text-white text-xs opacity-75">Items</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">
                          ${(customerGroup.totalAmount / customerGroup.totalTransactions).toFixed(0)}
                        </div>
                        <div className="text-white text-xs opacity-75">Avg/Visit</div>
                      </div>
                    </div>
                    
                    {/* Payment Methods Used */}
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-white text-sm opacity-75">Payment Methods:</span>
                      {Array.from(customerGroup.paymentMethods).map((method) => (
                        <span key={method} className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(method)}`}>
                          {getPaymentMethodText(method)}
                        </span>
                      ))}
                    </div>
                    
                    <div className="mt-2 text-white text-sm opacity-75">
                      Last Visit: {new Date(customerGroup.lastVisit).toLocaleDateString()}
                    </div>
                  </div>
                  
                  {/* Customer's Billing Records */}
                  <div className="p-4">
                    <h4 className="text-white font-semibold mb-3 flex items-center">
                      📋 Billing History ({customerGroup.records.length} records)
                    </h4>
                    <div className="space-y-2">
                      {customerGroup.records.map((record, recordIndex) => (
                        <motion.div
                          key={record.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (groupIndex * 0.1) + (recordIndex * 0.05) }}
                          onClick={() => {
                            setSelectedBilling(record);
                            setShowBillDetails(true);
                          }}
                          className="p-3 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2 text-sm">
                                <MapPin size={14} className="text-slate-400" />
                                <span className="text-slate-300">{record.tableNumber || 'N/A'}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-sm">
                                <Calendar size={14} className="text-slate-400" />
                                <span className="text-slate-300">{new Date(record.paidAt).toLocaleDateString()}</span>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(record.paymentMethod)}`}>
                                {getPaymentMethodText(record.paymentMethod)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-4">
                              <div className="text-right">
                                <div className="text-slate-400 text-xs">
                                  {record.numberOfOrders} orders • {record.totalItems} items
                                </div>
                                <div className="text-green-400 font-bold">${record.totalAmount.toFixed(2)}</div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBilling(record);
                                  setShowBillDetails(true);
                                }}
                                className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded transition-colors"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {groupedByCustomer.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👥</div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {searchTerm ? 'No Customers Found' : 'No Customer Data Yet'}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {searchTerm 
                      ? 'No customers found matching your search.' 
                      : 'Customer billing data will appear here when payments are completed.'}
                  </p>
                </div>
              )}
            </div>
          ) : viewMode === 'table' ? (
            /* Table View */
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">👤 Customer Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Table</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Payment</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Orders</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {filteredAndSortedBillingHistory.map((record, index) => (
                      <motion.tr
                        key={record.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-slate-750 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full flex items-center justify-center mr-3">
                              <User className="text-white" size={14} />
                            </div>
                            <div>
                              <div className="text-white font-bold text-lg">{record.customerName}</div>
                              <div className="text-slate-400 text-xs">ID: {record.customerId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-white">{record.tableNumber || 'N/A'}</div>
                          <div className="text-slate-400 text-xs">{record.tableLocation || ''}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-green-400 font-bold">${record.totalAmount.toFixed(2)}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(record.paymentMethod)}`}>
                            {getPaymentMethodText(record.paymentMethod)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-white">{record.numberOfOrders}</div>
                          <div className="text-slate-400 text-xs">{record.totalItems} items</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-white text-sm">{new Date(record.paidAt).toLocaleDateString()}</div>
                          <div className="text-slate-400 text-xs">{new Date(record.paidAt).toLocaleTimeString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => {
                              setSelectedBilling(record);
                              setShowBillDetails(true);
                            }}
                            className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded transition-colors"
                          >
                            View Details
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Card View */
            <div className="space-y-3">
              {filteredAndSortedBillingHistory.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    setSelectedBilling(record);
                    setShowBillDetails(true);
                  }}
                  className="p-4 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full flex items-center justify-center">
                        <User className="text-white" size={20} />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-xl">{record.customerName}</h3>
                        <p className="text-slate-400 text-sm">Customer ID: {record.customerId}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(record.paymentMethod)}`}>
                      {getPaymentMethodText(record.paymentMethod)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="flex items-center space-x-2 text-sm">
                      <MapPin size={16} className="text-slate-400" />
                      <span className="text-slate-300">{record.tableNumber || 'N/A'}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar size={16} className="text-slate-400" />
                      <span className="text-slate-300">{new Date(record.paidAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                    <div className="text-slate-400">
                      Orders: <span className="text-white font-semibold">{record.numberOfOrders}</span>
                    </div>
                    <div className="text-slate-400">
                      Items: <span className="text-white font-semibold">{record.totalItems}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                    <span className="text-slate-400 text-sm">Total Paid</span>
                    <span className="text-green-400 font-bold text-xl">${record.totalAmount.toFixed(2)}</span>
                  </div>
                  
                  <div className="mt-2 text-xs text-slate-500">
                    Paid at: {new Date(record.paidAt).toLocaleString()}
                  </div>
                </motion.div>
              ))}
              {filteredAndSortedBillingHistory.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📊</div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {searchTerm ? 'No Records Found' : 'No Billing History Yet'}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {searchTerm 
                      ? 'No billing records found matching your search.' 
                      : 'Billing records will appear here when customers complete payments.'}
                  </p>
                  {!searchTerm && (
                    <div className="bg-slate-700 rounded-lg p-4 max-w-md mx-auto text-left">
                      <p className="text-slate-300 text-sm mb-2">To create billing records:</p>
                      <ol className="text-slate-400 text-sm space-y-1 list-decimal list-inside">
                        <li>Login as a customer</li>
                        <li>Select a table</li>
                        <li>Place orders</li>
                        <li>Go to "Pay Bill & Exit"</li>
                        <li>Complete the payment</li>
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bill Details Modal */}
      {showBillDetails && selectedBilling && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowBillDetails(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-teal-500 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <User className="text-white" size={28} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedBilling.customerName}</h2>
                    <p className="text-white text-sm opacity-90">ID: {selectedBilling.customerId}</p>
                  </div>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${getPaymentMethodColor(selectedBilling.paymentMethod)}`}>
                  {getPaymentMethodText(selectedBilling.paymentMethod)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <p className="text-white text-xs opacity-75 mb-1">Table</p>
                  <p className="text-white font-semibold">{selectedBilling.tableNumber || 'N/A'}</p>
                  <p className="text-white text-xs opacity-75">{selectedBilling.tableLocation || ''}</p>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <p className="text-white text-xs opacity-75 mb-1">Total Orders</p>
                  <p className="text-white font-semibold text-2xl">{selectedBilling.numberOfOrders}</p>
                </div>
              </div>
            </div>

            {/* Billing Details */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-lg">Billing Details</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const printContent = `
                        BILLING RECEIPT
                        ================
                        Customer: ${selectedBilling.customerName}
                        ID: ${selectedBilling.customerId}
                        Table: ${selectedBilling.tableNumber || 'N/A'}
                        Date: ${new Date(selectedBilling.paidAt).toLocaleString()}
                        
                        Orders: ${selectedBilling.numberOfOrders}
                        Items: ${selectedBilling.totalItems}
                        Payment: ${getPaymentMethodText(selectedBilling.paymentMethod)}
                        ${selectedBilling.transactionId ? `Transaction: ${selectedBilling.transactionId}` : ''}
                        
                        Total: $${selectedBilling.totalAmount.toFixed(2)}
                      `;
                      const printWindow = window.open('', '_blank');
                      if (printWindow) {
                        printWindow.document.write(`<pre>${printContent}</pre>`);
                        printWindow.document.close();
                        printWindow.print();
                      }
                    }}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => {
                      const csvData = [
                        ['Field', 'Value'],
                        ['Customer Name', selectedBilling.customerName],
                        ['Customer ID', selectedBilling.customerId],
                        ['Table Number', selectedBilling.tableNumber || 'N/A'],
                        ['Table Location', selectedBilling.tableLocation || 'N/A'],
                        ['Total Amount', selectedBilling.totalAmount.toFixed(2)],
                        ['Payment Method', selectedBilling.paymentMethod],
                        ['Transaction ID', selectedBilling.transactionId || 'N/A'],
                        ['Number of Orders', selectedBilling.numberOfOrders.toString()],
                        ['Total Items', selectedBilling.totalItems.toString()],
                        ['Payment Date', new Date(selectedBilling.paidAt).toLocaleString()],
                        ['Session Start', selectedBilling.sessionStartTime ? new Date(selectedBilling.sessionStartTime).toLocaleString() : 'N/A']
                      ];
                      
                      const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `billing-${selectedBilling.customerId}-${selectedBilling.id}.csv`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                  >
                    Export
                  </button>
                </div>
              </div>

              {/* Quick Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-lg p-3 text-center">
                  <div className="text-blue-400 text-xs font-medium mb-1">ORDERS</div>
                  <div className="text-white text-xl font-bold">{selectedBilling.numberOfOrders}</div>
                </div>
                <div className="bg-gradient-to-r from-purple-900 to-pink-900 rounded-lg p-3 text-center">
                  <div className="text-purple-400 text-xs font-medium mb-1">ITEMS</div>
                  <div className="text-white text-xl font-bold">{selectedBilling.totalItems}</div>
                </div>
                <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-lg p-3 text-center">
                  <div className="text-green-400 text-xs font-medium mb-1">AVG/ORDER</div>
                  <div className="text-white text-xl font-bold">
                    ${(selectedBilling.totalAmount / selectedBilling.numberOfOrders).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Session Information */}
              <div className="bg-slate-700 rounded-lg p-4 mb-4">
                <h4 className="text-white font-semibold mb-3 flex items-center">
                  🍽️ Dining Session Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-xs mb-1">Table Assignment</p>
                    <p className="text-white font-semibold">
                      {selectedBilling.tableNumber} - {selectedBilling.tableLocation}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs mb-1">Session Status</p>
                    <p className="text-green-400 font-semibold">✅ Completed & Table Cleaned</p>
                  </div>
                  {selectedBilling.sessionStartTime && (
                    <>
                      <div>
                        <p className="text-slate-400 text-xs mb-1">Session Started</p>
                        <p className="text-white text-sm">{new Date(selectedBilling.sessionStartTime).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs mb-1">Session Duration</p>
                        <p className="text-white text-sm">
                          {Math.round((new Date(selectedBilling.paidAt).getTime() - new Date(selectedBilling.sessionStartTime).getTime()) / 60000)} minutes
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <div className="bg-slate-700 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-slate-400 text-xs mb-1">Payment Date</p>
                    <p className="text-white font-semibold">{new Date(selectedBilling.paidAt).toLocaleDateString()}</p>
                    <p className="text-slate-400 text-xs">{new Date(selectedBilling.paidAt).toLocaleTimeString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs mb-1">Payment Method</p>
                    <p className="text-white font-semibold">{getPaymentMethodText(selectedBilling.paymentMethod)}</p>
                  </div>
                </div>
                
                {selectedBilling.transactionId && (
                  <div className="border-t border-slate-600 pt-3">
                    <p className="text-slate-400 text-xs mb-1">Transaction ID</p>
                    <p className="text-white font-mono text-sm">{selectedBilling.transactionId}</p>
                  </div>
                )}
                
                <div className="border-t border-slate-600 pt-3 mt-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-slate-400 text-xs mb-1">Total Orders</p>
                      <p className="text-white font-semibold text-lg">{selectedBilling.numberOfOrders}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs mb-1">Total Items</p>
                      <p className="text-white font-semibold text-lg">{selectedBilling.totalItems}</p>
                    </div>
                  </div>
                </div>
                
                {selectedBilling.sessionStartTime && (
                  <div className="border-t border-slate-600 pt-3 mt-3">
                    <p className="text-slate-400 text-xs mb-1">Session Duration</p>
                    <p className="text-white text-sm">
                      Started: {new Date(selectedBilling.sessionStartTime).toLocaleString()}
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                      Duration: {Math.round((new Date(selectedBilling.paidAt).getTime() - new Date(selectedBilling.sessionStartTime).getTime()) / 60000)} minutes
                    </p>
                  </div>
                )}
              </div>

              {/* Order IDs */}
              <div className="bg-slate-700 rounded-lg p-4 mb-4">
                <p className="text-slate-400 text-xs mb-2">Order IDs ({selectedBilling.orderIds.length})</p>
                <div className="flex flex-wrap gap-2">
                  {selectedBilling.orderIds.map((orderId, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-600 rounded-full text-white text-xs font-mono">
                      #{orderId}
                    </span>
                  ))}
                </div>
              </div>

              {/* Total Summary */}
              <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-lg">Subtotal</span>
                  <span className="text-white text-lg">${(selectedBilling.totalAmount / 1.15).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300 text-sm">GST (5%)</span>
                  <span className="text-slate-300 text-sm">${((selectedBilling.totalAmount / 1.15) * 0.05).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-300 text-sm">Service Charge (10%)</span>
                  <span className="text-slate-300 text-sm">${((selectedBilling.totalAmount / 1.15) * 0.10).toFixed(2)}</span>
                </div>
                <div className="border-t border-white border-opacity-20 pt-4 flex items-center justify-between">
                  <span className="text-white font-bold text-2xl">Total Paid</span>
                  <span className="text-green-400 font-bold text-3xl">${selectedBilling.totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => setShowBillDetails(false)}
                className="w-full mt-6 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default AdminCustomerBilling;
